import { useEffect } from "react";
import { BUSINESS } from "@/data/business";

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

/** Title + description + OpenGraph + Twitter card tags. */
const applyDocumentMeta = ({ title, description, url, ogImage }) => {
  document.title = title;
  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:site_name", BUSINESS.name);
  upsertMeta("property", "og:image", ogImage);
  upsertMeta("property", "og:image:width", "1200");
  upsertMeta("property", "og:image:height", "630");
  upsertMeta("property", "og:image:alt", "Bahra Skincare, luxury facials and advanced skin treatments in Niles, IL");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", ogImage);
};

/** Canonical link element for the current route. */
const applyCanonical = (url) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
};

/** Build the LocalBusiness (DaySpa) structured-data payload. */
const buildLocalBusinessSchema = (origin) => ({
  "@context": "https://schema.org",
  "@type": "DaySpa",
  name: BUSINESS.name,
  description:
    "Licensed skincare specialist in Niles, IL offering personalized facials, Derma Pen treatments, chemical exfoliation, eyebrow shaping and tinting.",
  url: origin,
  telephone: "+17736001231",
  email: BUSINESS.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: "8307 W Golf Rd, Suite #144",
    addressLocality: "Niles",
    addressRegion: "IL",
    postalCode: "60714",
    addressCountry: "US",
  },
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Tuesday", opens: "10:00", closes: "15:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Wednesday", "Thursday", "Friday", "Saturday"], opens: "10:00", closes: "18:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Sunday", opens: "10:00", closes: "16:00" },
  ],
  sameAs: [BUSINESS.social.instagram, BUSINESS.social.facebook, BUSINESS.social.tiktok],
  priceRange: "$15 - $280",
});

/** Inject (or update) the LocalBusiness JSON-LD script tag. */
const applyLocalBusinessSchema = (origin) => {
  let script = document.getElementById("local-business-schema");
  if (!script) {
    script = document.createElement("script");
    script.id = "local-business-schema";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(buildLocalBusinessSchema(origin));
};

/**
 * Seo - per-route document metadata (title, description, OpenGraph,
 * Twitter cards, canonical URL) plus optional LocalBusiness JSON-LD.
 */
export const Seo = ({ title, description, path = "/", withSchema = false }) => {
  useEffect(() => {
    const origin = window.location.origin;
    const url = `${origin}${path}`;
    applyDocumentMeta({ title, description, url, ogImage: `${origin}/images/og-image.jpg` });
    applyCanonical(url);
    if (withSchema) applyLocalBusinessSchema(origin);
  }, [title, description, path, withSchema]);

  return null;
};
